/**
 * SpaceItemMessageCard
 *
 * Renders a compact clickable Space item reference inside chat messages.
 *
 * Primary responsibilities:
 * - Display Sidekick Space item context with type-aware iconography
 * - Route plain card opens and explicit new-tab opens separately
 *
 * Dependencies:
 * - Sidekick Space item context
 * - Tabler icons
 * - Shared class name utility
 */

'use client'

import type { DragEvent, KeyboardEvent, MouseEvent } from 'react'
import type { TablerIcon } from '@tabler/icons-react'
import {
  IconArchive,
  IconCalendar,
  IconCheckbox,
  IconExternalLink,
  IconFileText,
  IconFolder,
  IconLayoutDashboard,
  IconMessageCircle,
  IconMessages,
  IconNotes,
  IconPalette,
  IconPresentation,
  IconTable,
  IconViewportWide
} from '@tabler/icons-react'

import {
  isSidekickModalSpaceItemType,
  type SidekickSpaceItemContext
} from '@/lib/sidekick/space-item-context'
import { setSplitPaneDragData } from '@/lib/split-pane/targets'
import { cn } from '@/lib/utils'

export interface SpaceItemMessageCardProps {
  item: SidekickSpaceItemContext
  className?: string
  href?: string
  onOpen?: (item: SidekickSpaceItemContext) => void
  onOpenNewTab?: (item: SidekickSpaceItemContext) => void
}

const ITEM_TYPE_LABELS = {
  chat: 'Chat',
  collection: 'Collection',
  conversation: 'Conversation',
  design: 'Design',
  event: 'Event',
  file: 'File',
  folder: 'Folder',
  note: 'Note',
  space: 'Space',
  sheet: 'Sheet',
  sprint: 'Sprint',
  task: 'Task',
  whiteboard: 'Whiteboard'
} satisfies Record<SidekickSpaceItemContext['type'], string>

const ITEM_TYPE_ICONS = {
  chat: IconMessages,
  collection: IconArchive,
  conversation: IconMessageCircle,
  design: IconPalette,
  event: IconCalendar,
  file: IconFileText,
  folder: IconFolder,
  note: IconNotes,
  space: IconLayoutDashboard,
  sheet: IconTable,
  sprint: IconPresentation,
  task: IconCheckbox,
  whiteboard: IconViewportWide
} satisfies Record<SidekickSpaceItemContext['type'], TablerIcon>

const SPACE_ITEM_MESSAGE_CARD_CLASS =
  'group relative my-1.5 flex w-full max-w-full items-start gap-2.5 overflow-hidden rounded-sm bg-transparent py-1.5 pl-3 pr-2 text-left text-foreground shadow-none motion-safe:transition-[background-color] motion-safe:duration-150 hover:bg-muted/20 motion-reduce:transition-none'

const SPACE_ITEM_MESSAGE_MARKER_CLASS = 'absolute inset-y-1.5 left-0 w-px bg-border'

const SPACE_ITEM_MESSAGE_ICON_CLASS =
  'flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground'

const SPACE_ITEM_MESSAGE_ACTION_CLASS =
  'relative flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:transition-[background-color,color,opacity] motion-safe:duration-150 hover:bg-muted hover:text-foreground group-hover:opacity-100 motion-reduce:transition-none'

const getVisibleText = (value: string | null | undefined, fallback: string) => {
  const trimmedValue = value?.trim()

  return trimmedValue || fallback
}

export function SpaceItemMessageCard({
  item,
  className,
  href,
  onOpen,
  onOpenNewTab
}: SpaceItemMessageCardProps) {
  const Icon = ITEM_TYPE_ICONS[item.type]
  const typeLabel = ITEM_TYPE_LABELS[item.type]
  const title = getVisibleText(item.title, typeLabel)
  const summary = getVisibleText(item.summary, '')
  const canOpenInNewTab = Boolean(onOpenNewTab || href)
  const canOpenInline = Boolean(onOpen && isSidekickModalSpaceItemType(item.type))
  const shouldOpenPrimaryLinkInNewTab = Boolean(href && !canOpenInline)
  const cardHref = href ?? '#'

  const openItem = () => {
    onOpen?.(item)
  }

  const handleCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !onOpen ||
      !canOpenInline ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    event.preventDefault()
    openItem()
  }

  const handleCardKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (!canOpenInline || (event.key !== ' ' && event.key !== 'Enter')) {
      return
    }

    event.preventDefault()
    openItem()
  }

  const handleOpenNewTab = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.stopPropagation()

    if (onOpenNewTab) {
      event.preventDefault()
      onOpenNewTab(item)
    }
  }

  const handleDragStart = (event: DragEvent<HTMLSpanElement>) => {
    if (!href) return

    setSplitPaneDragData(event.dataTransfer, href)
    event.dataTransfer.setData('text/plain', href)
    event.dataTransfer.effectAllowed = 'copy'
  }

  return (
    <span
      data-testid="space-item-message-card"
      className={cn(
        href && 'cursor-grab active:cursor-grabbing',
        SPACE_ITEM_MESSAGE_CARD_CLASS,
        className
      )}
      draggable={Boolean(href)}
      onDragStart={handleDragStart}
    >
      <span
        aria-hidden="true"
        data-testid="space-item-message-card-rail"
        className={SPACE_ITEM_MESSAGE_MARKER_CLASS}
      />

      <a
        aria-label={`Open ${typeLabel.toLowerCase()} ${title}`}
        className="focus-visible:ring-ring relative flex min-w-0 flex-1 items-start gap-2.5 rounded-sm focus-visible:outline-none focus-visible:ring-2"
        href={cardHref}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        rel={shouldOpenPrimaryLinkInNewTab ? 'noopener noreferrer' : undefined}
        target={shouldOpenPrimaryLinkInNewTab ? '_blank' : undefined}
      >
        <span data-testid="space-item-message-card-icon" className={SPACE_ITEM_MESSAGE_ICON_CLASS}>
          <Icon aria-hidden="true" className="size-4" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="text-foreground min-w-0 max-w-full truncate text-sm font-medium">
              {title}
            </span>
            <span aria-hidden="true" className="text-muted-foreground/70 text-xs">
              ·
            </span>
            <span className="text-muted-foreground shrink-0 text-xs">{typeLabel}</span>
          </span>

          {summary ? (
            <span className="text-muted-foreground mt-0.5 line-clamp-2 block text-xs leading-5">
              {summary}
            </span>
          ) : null}
        </span>
      </a>

      {canOpenInNewTab ? (
        href ? (
          <a
            aria-label={`Open ${typeLabel.toLowerCase()} ${title} in a new tab`}
            className={SPACE_ITEM_MESSAGE_ACTION_CLASS}
            href={href}
            onClick={handleOpenNewTab}
            rel="noopener noreferrer"
            target="_blank"
          >
            <IconExternalLink aria-hidden="true" className="size-4" />
          </a>
        ) : (
          <button
            aria-label={`Open ${typeLabel.toLowerCase()} ${title} in a new tab`}
            className={SPACE_ITEM_MESSAGE_ACTION_CLASS}
            onClick={handleOpenNewTab}
            type="button"
          >
            <IconExternalLink aria-hidden="true" className="size-4" />
          </button>
        )
      ) : null}
    </span>
  )
}
